import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  chakra,
  Box,
  Text,
  Stack,
  HStack,
  Select,
} from "@chakra-ui/react";
import { TriangleDownIcon, TriangleUpIcon } from "@chakra-ui/icons";

import {
  useReactTable,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel
} from "@tanstack/react-table";
import { rankItem } from "@tanstack/match-sorter-utils";

import usePaginatedFetch from "./usePaginatedFetch";

const fuzzyFilter = (row, columnId, value, addMeta) => {
  // Rank the item
  const itemRank = rankItem(row.getValue(columnId), value);

  // Store the itemRank info
  addMeta({
    itemRank,
  });

  // Return if the item should be filtered in/out
  return itemRank.passed;
};

export default function DataTable({
  data,
  totalRecords,
  columns,
  searchInput,
  onClickRow,
  initialSort = [],
  filters,
  paginationState,
  fetchData,
}) {
  const initialSortMemo = useMemo(() => initialSort, [initialSort]);
  const [sorting, setSorting] = useState(initialSortMemo);
  const [globalFilter, setGlobalFilter] = useState("");
  const [pagination, setPagination, savePagination] = paginationState;

  usePaginatedFetch({ fetchData, pagination, filters, savePagination });

  // if sorting is cleared, reset to initial sort
  useEffect(() => {
    if (sorting.length === 0 && initialSortMemo.length > 0) {
      setSorting(initialSortMemo);
    }
  }, [sorting, initialSortMemo]);

  // if search input changes, update global filter
  useEffect(() => {
    setGlobalFilter(searchInput);
  }, [searchInput]);

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    manualPagination: true,
    rowCount: totalRecords,

    state: {
      //sorting,
      globalFilter,
      pagination,
    },
    globalFilterFn: fuzzyFilter,
    onGlobalFilterChange: setGlobalFilter,
  });

  return (
    <Stack w="full">
      <Box overflowX="auto">
        <Table style={{
          border: '1px solid #898989',
          borderRadius: "12px",
          overflow: "hidden",
          backgroundColor: '#898989'
        }}>
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const meta = header.column.columnDef.meta;
                  const styles = {
                    ...{
                      width: header.column.getSize(),
                    }, ...(meta?.styles || {})
                  };
                  return (
                    <Th
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      isNumeric={meta?.isNumeric}
                      style={styles}
                      sx={{
                        fontSize: '15px',
                        fontWeight: "600",
                        borderBottom: '1px solid #898989',
                        textTransform: 'capitalize'
                      }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}

                      <chakra.span pl="4">
                        {header.column.getIsSorted() ? (
                          header.column.getIsSorted() === "desc" ? (
                            <TriangleDownIcon aria-label="sorted descending" />
                          ) : (
                            <TriangleUpIcon aria-label="sorted ascending" />
                          )
                        ) : null}
                      </chakra.span>
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => (
              <Tr
                key={row.id}
                cursor={onClickRow ? "pointer" : "default"}
                onClick={onClickRow ? () => onClickRow(row?.original, searchInput) : null}
              >
                {row.getVisibleCells().map((cell) => {
                  const meta = cell.column.columnDef.meta;
                  const styles = {
                    ...{
                      width: cell.column.getSize(),
                    }, ...(meta?.styles || {})
                  };
                  return (
                    <Td
                      key={cell.id}
                      isNumeric={meta?.isNumeric}
                      style={styles}
                      sx={{
                        fontSize: '14px',
                        fontWeight: "400",
                        borderBottom: '1px solid #898989'
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Td>
                  );
                })}
              </Tr>
            ))}
            {/* display message if empty */}
            {table.getRowModel().rows.length === 0 && (
              <Tr>
                <Td colSpan={columns.length}>No records</Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>
      {table.getRowCount() > 0 && (
        <HStack pb="3em">
          <Text mr="4em">
            Showing&nbsp;
            {(table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1).toLocaleString() + "-" + (table.getState().pagination.pageIndex * table.getState().pagination.pageSize + table.getRowModel().rows.length).toLocaleString()}
            &nbsp;of&nbsp;
            {table.getRowCount().toLocaleString()}
          </Text>
          <HStack>
            <Text whiteSpace="nowrap">Items per page</Text>
            <Select
              value={table.getState().pagination.pageSize}
              onChange={e => {
                table.setPageSize(Number(e.target.value))
              }}
            >
              {[10, 25, 50, 100].map((current, idx, array) => (
                (idx === 0 || table.getRowCount() > array[idx - 1]) && (
                  <option key={current} value={current} style={{ color: 'black' }}>{current}</option>
                )
              ))}
            </Select>
          </HStack>
          <HStack ml="auto">
            <button
              className="border rounded p-1"
              onClick={() => table.firstPage()}
              disabled={!table.getCanPreviousPage()}
            >
              {'<<'}
            </button>
            <button
              className="border rounded p-1"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              {'<'}
            </button>
            <HStack>
              <Text>
                Page&nbsp;
                {table.getState().pagination.pageIndex + 1}
                &nbsp;of&nbsp;
                {table.getPageCount().toLocaleString()}
              </Text>
            </HStack>
            <button
              className="border rounded p-1"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              {'>'}
            </button>
            <button
              className="border rounded p-1"
              onClick={() => table.lastPage()}
              disabled={!table.getCanNextPage()}
            >
              {'>>'}
            </button>
          </HStack>
        </HStack>
      )}
    </Stack>
  );
}
